import React, { useEffect, useRef } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { LICENCE_KEY } from "../constants/charts";
import { COLOR_GRAY_BANNER_BACKGROUND } from "../constants/general-constants";
import {TRUE} from "../constants/general-constants";

am4core.addLicense(LICENCE_KEY);

export default function AmChartStacked({
  chartId,
  categoryKey,
  chartData,
  chartKeys,
  height,
  width,
  valueAxisTitle = "",
  showScrollbar,
  maxInitialPoints = 400,
  targetLines,
  hideLabels,
  legendPosition,
  yMax
}) {
  const ctxRef = useRef({
    chart: null,
    xAxis: null,
    yAxis: null,
    seriesById: {},
    rafId: null
  });
  const holderRef = useRef(null);
  const didInitRef = useRef(false);

  // Keep ONLY target line artifacts here so we can dispose safely
  // { range, label, sizeDisp, extremeDisp }
  const targetArtifactsRef = useRef([]);

  useEffect(function () {
	
    if (didInitRef.current) return;
    didInitRef.current = true;
    if (!holderRef.current) return;

    const chart = am4core.create(holderRef.current, am4charts.XYChart);
    ctxRef.current.chart = chart;

    chart.animationsEnabled = true;
    am4core.options.queue = false;
    chart.paddingLeft = 200;
    chart.paddingRight = 100;
    chart.fontSize = 11;
    
    const xAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    xAxis.dataFields.category = categoryKey;
    xAxis.renderer.minGridDistance = 30;
    xAxis.renderer.labels.template.fill = am4core.color("#000");
    xAxis.renderer.labels.template.rotation = -90;
    xAxis.renderer.labels.template.horizontalCenter = "right";
    xAxis.renderer.labels.template.fontSize = 9;
    xAxis.renderer.cellStartLocation = 0.1;
    xAxis.renderer.cellEndLocation = 0.9;

    const yAxis = chart.yAxes.push(new am4charts.ValueAxis());
    yAxis.renderer.labels.template.fill = am4core.color("#000");
    yAxis.title.text = valueAxisTitle || "";
    yAxis.title.fill = am4core.color("#000");
    yAxis.title.fontSize = 14;
    yAxis.min = 0;

    chart.paddingLeft = 0;
    yAxis.marginLeft = 0;
    yAxis.renderer.minLabelPosition = 0;
    yAxis.renderer.maxLabelPosition = 1;
	
	xAxis.renderer.grid.template.disabled = true;
	yAxis.renderer.grid.template.disabled = true;
	
	xAxis.renderer.axisFills.template.disabled = false;
	xAxis.renderer.axisFills.template.fillOpacity = 0.08;
	xAxis.renderer.axisFills.template.strokeOpacity = 0;
	xAxis.renderer.axisFills.template.fill = am4core.color("#DADADA");
	xAxis.renderer.axisFills.template.location = 0.5; 

    chart.numberFormatter.numberFormat = "#.##a";
    chart.numberFormatter.bigNumberPrefixes = [
      { number: 1e3, suffix: "K" },
      { number: 1e6, suffix: "M" },
      { number: 1e9, suffix: "Bn" }
    ];

    ctxRef.current.xAxis = xAxis;
    ctxRef.current.yAxis = yAxis;
    ctxRef.current.seriesById = {};

    return function cleanup() {
      // dispose any target artifacts we created
      try {
        if (targetArtifactsRef.current && targetArtifactsRef.current.length) {
          targetArtifactsRef.current.forEach(function (t) {
            try { t.sizeDisp && t.sizeDisp.dispose(); } catch {}
            try { t.extremeDisp && t.extremeDisp.dispose(); } catch {}
            try { t.label && !t.label.isDisposed() && t.label.dispose(); } catch {}
            try { t.range && !t.range.isDisposed() && t.range.dispose(); } catch {}
          });
        }
      } catch {}
      targetArtifactsRef.current = [];

      if (ctxRef.current.rafId != null) {
        try { cancelAnimationFrame(ctxRef.current.rafId); } catch {}
        ctxRef.current.rafId = null;
      }
      try { chart.dispose(); } catch {}
      ctxRef.current = { chart: null, xAxis: null, yAxis: null, seriesById: {}, rafId: null };
      didInitRef.current = false;
    };
  }, []);
  
  useEffect(function () {
  const xAxis = ctxRef.current.xAxis;
  if (!xAxis || xAxis.isDisposed()) return;

  if (hideLabels && hideLabels === TRUE) {
    xAxis.renderer.labels.template.disabled = true;
    xAxis.renderer.minLabelPosition = 0; // squash padding
    xAxis.renderer.maxLabelPosition = 1;
    xAxis.renderer.height = 0; // no reserved space
    ctxRef.current.paddingBottom = 10;
  } else {
    xAxis.renderer.labels.template.disabled = false;
    xAxis.renderer.height = undefined; // restore auto height
  }
}, [hideLabels]);

  useEffect(function () {
    const chart = ctxRef.current.chart;
    if (!chart || chart.isDisposed()) return;

    if (chart.scrollbarX && !showScrollbar) {
      try { chart.scrollbarX.dispose(); } catch {}
      chart.scrollbarX = null;
    } else if (!chart.scrollbarX && showScrollbar) {
      chart.scrollbarX = new am4core.Scrollbar();
    }
  }, [showScrollbar]);

  useEffect(function () {
    const yAxis = ctxRef.current.yAxis;
    if (!yAxis || yAxis.isDisposed()) return;
    yAxis.title.text = valueAxisTitle || "";
  }, [valueAxisTitle]);


  useEffect(function () {
    const chart = ctxRef.current.chart;
    if (!chart || chart.isDisposed()) return;

    if (legendPosition) {
      if (!chart.legend) chart.legend = new am4charts.Legend();
      chart.legend.position = legendPosition;
      chart.legend.markers.template.width = 10;
      chart.legend.markers.template.height = 10;
     //chart.legend.useDefaultMarker = true;
      const marker = chart.legend.markers.template.createChild(am4core.Circle);
      marker.width = 10;
      marker.height = 10;
      chart.legend.itemContainers.template.paddingTop = 2;
      chart.legend.itemContainers.template.paddingBottom = 2;
      chart.legend.itemContainers.template.paddingLeft = 5;
      chart.legend.itemContainers.template.paddingRight = 5;
      chart.legend.labels.template.fontSize = 12;
      chart.legend.valueLabels.template.fontSize = 12;
      chart.legend.maxHeight = 50;
      chart.legend.scrollable = true;
    } else {
      chart.legend = undefined;
    }
  }, [legendPosition]);

  useEffect(function () {
    const ctx = ctxRef.current;
    const chart = ctx.chart;
    if (!chart || chart.isDisposed() || !Array.isArray(chartKeys)) return;

    const wanted = new Set(chartKeys.map(k => k.id));
    const byId = ctx.seriesById || {};

    // Safety net to dispose removed series if you ever change chartKeys:
    /*
    Object.keys(byId).forEach(function (id) {
      if (!wanted.has(id)) {
        try { byId[id].dispose(); } catch {}
        delete byId[id];
      }
    });
    */

	
	
    chartKeys.forEach(function (k) {
      let series = byId[k.id];
      if (!series) {
        series = chart.series.push(new am4charts.ColumnSeries());
        byId[k.id] = series;
        series.stacked = true;
        series.dataFields.categoryX = categoryKey;
        series.dataFields.valueY = k.id;
        series.columns.template.width = am4core.percent(100);
        series.columns.template.strokeOpacity = 1;
        series.columns.template.strokeWidth = 1;
        series.columns.template.fillOpacity = 1;
        series.sequencedInterpolation = false;
        series.interpolationDuration = 500;
        series.interpolationEasing = am4core.ease.quadOut;
        series.columns.template.tooltipText = "{categoryX}\n{name}: {valueY.value}";
      }
      series.name = k.title;
	  if (k.color) {
	    const col = am4core.color(k.color);

	    series.fill = col;
	    series.stroke = col;

	    series.columns.template.fill = col;
	    series.columns.template.stroke = col;

	    // 🔥 IMPORTANT: force override
	    series.columns.template.propertyFields.fill = undefined;
	    series.columns.template.propertyFields.stroke = undefined;
	  }
    });
  }, [chartKeys, categoryKey]);

  // ---------- TARGET LINES (v4-safe) ----------
  useEffect(function () {
    const chart = ctxRef.current.chart;
    const yAxis = ctxRef.current.yAxis;
    if (!chart || chart.isDisposed() || !yAxis || yAxis.isDisposed()) return;

    // Dispose previous artifacts we created
    try {
      if (targetArtifactsRef.current && targetArtifactsRef.current.length) {
        targetArtifactsRef.current.forEach(function (t) {
          try { t.sizeDisp && t.sizeDisp.dispose(); } catch {}
          try { t.extremeDisp && t.extremeDisp.dispose(); } catch {}
          try { t.label && !t.label.isDisposed() && t.label.dispose(); } catch {}
          try { t.range && !t.range.isDisposed() && t.range.dispose(); } catch {}
        });
      }
    } catch {}
    targetArtifactsRef.current = [];

    if (!Array.isArray(targetLines) || targetLines.length === 0) {
      chart.paddingRight = 0;
      try { yAxis.axisRanges && yAxis.axisRanges.clear(); } catch {}
      return;
    }
		
    chart.paddingRight = 100;
    try { yAxis.axisRanges && yAxis.axisRanges.clear(); } catch {}

    function positionLabel(label, value) {
      if (!chart || chart.isDisposed() || !yAxis || yAxis.isDisposed() || !label || label.isDisposed()) {
        return;
      }
      // value -> position (0..1) -> pixel coord
      var pos = yAxis.valueToPosition(value);
      var yPx = yAxis.renderer.positionToCoordinate(pos);
      label.x = chart.plotContainer.pixelWidth + 10; // inset 10px from right
      label.y = yPx;
      label.horizontalCenter = "left";
      label.verticalCenter = "middle";
    }

    targetLines.forEach(function (t) {
      if (!t || typeof t.value !== "number") return;

      // 1) axis range for the line
      const range = yAxis.axisRanges.create();
      range.value = t.value;
      //range.grid.stroke = am4core.color(COLOR_GRAY_BANNER_BACKGROUND);
      //range.grid.strokeWidth = 1;
      //range.grid.strokeOpacity = 1;
      range.grid.above = true;
      // no endValue in v4 for a single line
      

      // 2) floating label (plotContainer child)
      const label = range.label;
		  label.text = t.title ?? String(t.value);
		  label.fontSize = 12;
		  label.fill = am4core.color("#fff");
		  label.padding(6, 10, 6, 10);
		
		  //  simple solid black rounded background (old style)
		  label.background.fill = am4core.color("#000");
		 // label.background.stroke = am4core.color("#000");
			//label.background.cornerRadius(6, 6, 6, 6);
		  label.background.fillOpacity = 1; 
		  label.background.strokeOpacity = 1;
		
		  label.horizontalCenter = "left";
		  label.verticalCenter = "middle";
	
		   const setRightDx = function () {
			    const w = chart.plotContainer.pixelWidth || 0;
			    label.dx = w + 90; // 10px in from right edge
			  };
			  setRightDx();
			  chart.events.on("sizechanged", setRightDx);
			
			// make sure it's on top
			label.zIndex = 1000;

    });
  }, [targetLines]);

  // ---------- Y MAX ----------
  useEffect(function () {
    const yAxis = ctxRef.current.yAxis;
    const chart = ctxRef.current.chart;
    if (!chart || chart.isDisposed() || !yAxis || yAxis.isDisposed()) return;

    if (yMax && typeof yMax === "number") {
      yAxis.max = Math.ceil(yMax * 1.1);
      yAxis.strictMinMax = true;

      chart.paddingLeft = 0;
      yAxis.marginLeft = 0;
      yAxis.renderer.minLabelPosition = 0;
      yAxis.renderer.maxLabelPosition = 1;

      try { chart.validateLayout(); } catch (e) { console.warn("Chart validateLayout failed:", e); }
    } else {
      // unset strict max if you remove yMax
      yAxis.max = undefined;
      yAxis.strictMinMax = false;
    }
  }, [yMax]);

  // ---------- DATA UPDATE ----------
  useEffect(function () {
    const ctx = ctxRef.current;
    const chart = ctx.chart;
    const xAxis = ctx.xAxis;
    if (!chart || chart.isDisposed() || !xAxis) return;

    const keys = Array.isArray(chartKeys) ? chartKeys.map(k => k.id) : [];
    const dataIn = Array.isArray(chartData) ? chartData : [];

    if (ctx.rafId != null) {
      try { cancelAnimationFrame(ctx.rafId); } catch {}
      ctx.rafId = null;
    }

    if (dataIn.length === 0) {
      chart.data = [];
	  chart.invalidateRawData();
      return;
    }

    const clean = dataIn.map(function (row, idx) {
      const out = Object.assign({}, row);
      if (out[categoryKey] == null || out[categoryKey] === "") {
        out[categoryKey] = out.title || out.name || ("cat_" + idx);
      }
      keys.forEach(function (k) {
        const v = out[k];
        out[k] = typeof v === "number" ? v : (v == null ? 0 : Number(v));
        if (Number.isNaN(out[k])) out[k] = 0;
      });
      return out;
    });

    const total = clean.length;
    const bigSet = total > 800;

    xAxis.renderer.labels.template.disabled = !!hideLabels || bigSet;
    xAxis.renderer.grid.template.disabled = bigSet;

    const initial = total > maxInitialPoints ? clean.slice(0, maxInitialPoints) : clean;
    chart.data = initial;
	chart.invalidateRawData();
	

    if (total > maxInitialPoints) {
      ctx.rafId = requestAnimationFrame(function () {
        if (!ctxRef.current.chart || chart.isDisposed()) return;
        chart.data = clean;
        chart.invalidateData();
        ctx.rafId = null;
      });
    }
  }, [chartId, chartData, chartKeys, categoryKey, maxInitialPoints, hideLabels]);

  return (
    <div id={chartId} ref={holderRef} style={{ width: width, height: height }} />
  );
}
